/**
 * component-bundle-products.js — Block B (_bundle-products) interactions.
 *
 * Loaded (defer, after component-bundle-core.js) by the parent block; the
 * window guard dedupes across instances (Rule J12). Everything is
 * document-level delegated (J1/J2) keyed off closest('[data-bundle-prods]')
 * so listeners survive SRA root swaps with no re-bind.
 *
 * Owns the checkbox-row mechanics (slice-23 §5.5): click / Space / Enter
 * toggling (clicks inside the variant <select> never toggle; locked/sold-out
 * rows are aria-disabled and inert), the integer-cents totals engine, the
 * summary stamp (count + unit word from the root's data-bp-unit-s/-p + old
 * total; product names removed U6 §20.6), the achieved-discount chip + nudge,
 * the CTA add flow, the per-row variant select (restamp + state), and the list
 * vertical cap.
 *
 * Offers (U6 §20 + U6-F1): tiered levels only — resolveOffer(root, count) is
 * the single pricing source; recompute consumes its {pct, code, next} and
 * handleCta applies its code, so display and applied code never diverge. The
 * discount applies only when ≥1 companion is checked (the U4 guard, the
 * flat-equivalence anchor) at the achieved active level's pct. (U6-F1 removed
 * per-product mode + the live re-stamped header badge; the badge is now a
 * static server "up to" max and the live "−X%" lives in the achieved chip.)
 * The main product is counted from the ROOT attrs (data-include-main/-main-
 * cents) even when its row is hidden, and excluded while data-main-soldout is
 * present. Sold-out rows are excluded in both bp_soldout modes. Integer cents.
 *
 * Core (window.theme.bundle) supplies formatMoney / addItems / afterAdd /
 * the shared state map. Core's own product:variant-changed listener is
 * [data-bundle-qty]-scoped (Block A) — this file registers its OWN restore
 * listener (§19.4: no core edit). State shape (S23-P4-PLAN §4.7):
 *   theme.bundle.state[blockId] = {
 *     checked:  { [rowId]: bool },   // rowId = child block.id | 'main'
 *     variants: { [rowId]: variantIdNumber }
 *   }
 *
 * U7 popup: the B shell ships (§2.7/§19.4) and this file gains its ONE popup
 * hook — the bundle:popup-opened refresh listener (re-measures the now-visible
 * pop-body). The popup-control attrs reuse A's data-bq-* JS vocabulary so the
 * existing core listeners serve B (plan Q1); the closeBundleOverlays() call in
 * the CTA flow was already wired (inert inline — closest('dialog.overlay')
 * matches only once the body is teleported into the overlay).
 */

(function () {
  'use strict';

  if (window.__kvBundleProds) return;
  window.__kvBundleProds = true;

  function core() {
    return window.theme && window.theme.bundle;
  }

  function rowsOf(root) {
    return Array.prototype.slice.call(root.querySelectorAll('[data-bpp]'));
  }

  function isSoldout(row) {
    return row.classList.contains('bundle-prods__row--soldout');
  }

  function isLocked(row) {
    return row.getAttribute('aria-disabled') === 'true';
  }

  function isOn(row) {
    return row.classList.contains('is-on');
  }

  function isMain(row) {
    return row.getAttribute('data-row-id') === 'main';
  }

  function isCards(root) {
    return root.classList.contains('bundle-prods--cards');
  }

  function getScroller(root) {
    return root.querySelector('.bundle-prods__scroller');
  }

  /** Disabled state at the scroll ends (§16). LTR store: scrollLeft semantics. */
  function syncEnds(root) {
    var sc = getScroller(root);
    var prev = root.querySelector('.bundle-prods__arrow--prev');
    var next = root.querySelector('.bundle-prods__arrow--next');
    if (!sc || !prev || !next) return;
    prev.disabled = sc.scrollLeft <= 1;
    next.disabled = sc.scrollLeft >= sc.scrollWidth - sc.clientWidth - 1;
  }

  /** Real-overflow detection -> wake/park the arrows. Cards root + data-arrows=
      "auto" only (§1.4); ported from A's syncArrows (component-bundle-quantity.js). */
  function syncArrows(root) {
    if (!isCards(root)) return;
    var sc = getScroller(root);
    var prev = root.querySelector('.bundle-prods__arrow--prev');
    var next = root.querySelector('.bundle-prods__arrow--next');
    if (!sc || !prev || !next) return;
    var show = root.getAttribute('data-arrows') === 'auto' && sc.scrollWidth > sc.clientWidth + 1;
    [prev, next].forEach(function (b) {
      b.hidden = !show;
      if (show) {
        b.removeAttribute('aria-hidden');
        b.removeAttribute('tabindex');
      } else {
        b.setAttribute('aria-hidden', 'true');
        b.setAttribute('tabindex', '-1');
      }
    });
    if (show) syncEnds(root);
  }

  /** Sharp cards (advisor §12-C, route c): a card image displays at ~basis width
      (far wider than the list's fixed 96px box), so set each card image's `sizes`
      to its measured CSS width — the browser then pulls a sharp candidate from the
      widened srcset ladder (the snippet now tops at 512w). Cards roots only (list
      keeps sizes='96px'); SRA-safe (runs from initRoot + the debounced resize);
      hidden rows measure 0 and are skipped. */
  function applyCardImageSizes(root) {
    if (!isCards(root)) return;
    root.querySelectorAll('.bundle-prods__img-el').forEach(function (img) {
      var w = Math.round(img.getBoundingClientRect().width);
      if (w > 0) img.sizes = w + 'px';
    });
  }

  /** Unit price: the selected <option>'s data-cents when a select exists,
      else the server-rendered row data-cents. Integer cents only. */
  function unitCents(row) {
    var sel = row.querySelector('[data-bpp-vsel]');
    if (sel && sel.selectedIndex > -1) {
      var c = parseInt(sel.options[sel.selectedIndex].getAttribute('data-cents'), 10);
      if (!isNaN(c)) return c;
    }
    return parseInt(row.getAttribute('data-cents'), 10) || 0;
  }

  function rowCents(row) {
    return unitCents(row) * (parseInt(row.getAttribute('data-qty'), 10) || 1);
  }

  /** Variant id for the cart add: select value, else the server-rendered
      data-variant-id (single-variant rows have no select). */
  function variantIdOf(row) {
    var sel = row.querySelector('[data-bpp-vsel]');
    if (sel && sel.value) return parseInt(sel.value, 10);
    return parseInt(row.getAttribute('data-variant-id'), 10) || null;
  }

  function checkedCompanions(root) {
    return rowsOf(root).filter(function (row) {
      return !isMain(row) && !isSoldout(row) && isOn(row);
    });
  }

  /** Displayed count (§20.2): checked companions + the counted main. Shared by
      recompute and handleCta so the resolver sees the same number in both. */
  function countOf(root) {
    var includeMain = root.getAttribute('data-include-main') === 'true';
    var mainCounted = includeMain && !root.hasAttribute('data-main-soldout');
    return checkedCompanions(root).length + (mainCounted ? 1 : 0);
  }

  /** Parse the root's level attrs into {n, min, pct, code}. Compact "min:pct"
      pairs (no JSON — each code rides its own escaped attr); the level-1 code
      reuses the existing data-code attr. Inactive levels emit no data-bp-lN
      attr (U6-F1), so missing/malformed levels are skipped here for free. */
  function levelData(root) {
    var codes = {
      1: root.getAttribute('data-code') || '',
      2: root.getAttribute('data-bp-l2-code') || '',
      3: root.getAttribute('data-bp-l3-code') || ''
    };
    var out = [];
    [1, 2, 3].forEach(function (n) {
      var raw = root.getAttribute('data-bp-l' + n);
      if (!raw) return;
      var p = raw.split(':');
      var min = parseInt(p[0], 10);
      var pct = parseInt(p[1], 10);
      if (isNaN(min) || isNaN(pct)) return;
      out.push({ n: n, min: min, pct: pct, code: codes[n] });
    });
    return out;
  }

  /**
   * The ONE pricing resolver (§20.8): both the displayed chip/code read this,
   * so they can never diverge by construction. Tiered levels only (U6-F1
   * removed per-product mode). Among ACTIVE levels with pct > 0, the highest
   * min <= count wins (ties on min -> higher level number); `next` = the nearest
   * higher level that beats the current pct (the nudge target, better-offer
   * guard). Inactive levels emit no data-bp-lN attr, so they never enter here.
   *
   * Count vs application: `count` is the DISPLAYED count (companions + counted
   * main). resolveOffer reports the level reached at that count, but recompute
   * additionally requires >= 1 checked companion before it APPLIES the pct (the
   * U4 guard) — a main-only count resolves a pct yet never discounts.
   */
  function resolveOffer(root, count) {
    var levels = levelData(root);
    var achieved = null;
    levels.forEach(function (lv) {
      if (lv.pct > 0 && lv.min <= count &&
          (!achieved || lv.min > achieved.min || (lv.min === achieved.min && lv.n > achieved.n))) {
        achieved = lv;
      }
    });
    var pct = achieved ? achieved.pct : 0;
    var code = achieved ? achieved.code : '';
    var next = null;
    levels.forEach(function (lv) {
      if (lv.min > count && lv.pct > pct && (!next || lv.min < next.min)) next = lv;
    });
    return { pct: pct, code: code, next: next ? { needed: next.min - count, pct: next.pct } : null };
  }

  /** Row line price = unit × qty, undiscounted. (U6-F1: per-product was/now
      removed — levels apply a single % to the bundle total, not per row.) */
  function stampRowPrice(row, root) {
    var el = row.querySelector('[data-bpp-price]');
    var engine = core();
    if (!el || !engine) return;
    el.textContent = engine.formatMoney(rowCents(row), root.getAttribute('data-money-format'));
  }

  /**
   * Totals + summary + CTA stamp (§5.5, integer cents only). The main
   * product counts from the ROOT attrs — even when its row is hidden
   * (bp_show_main off) — and never while data-main-soldout is present.
   */
  function recompute(root) {
    var engine = core();
    var fmt = root.getAttribute('data-money-format');
    var includeMain = root.getAttribute('data-include-main') === 'true';
    var mainCounted = includeMain && !root.hasAttribute('data-main-soldout');
    var companions = checkedCompanions(root);
    var count = companions.length + (mainCounted ? 1 : 0);
    var mainCents = mainCounted ? parseInt(root.getAttribute('data-main-cents'), 10) || 0 : 0;

    var offer = resolveOffer(root, count);

    // sum = undiscounted (struck old total); total = what the customer pays.
    var sum = mainCents;
    companions.forEach(function (row) { sum += rowCents(row); });
    // The U4 guard — the flat-equivalence anchor: a main-only count never
    // discounts even when its level resolves a pct (>= 1 companion required).
    var applies = companions.length > 0 && offer.pct > 0;
    var total = applies ? Math.round((sum * (100 - offer.pct)) / 100) : sum;

    var countEl = root.querySelector('[data-bp-count]');
    if (countEl) {
      // French takes the singular at 0 — plural only above 1. (Names removed §20.6.)
      var word = root.getAttribute(count > 1 ? 'data-bp-unit-p' : 'data-bp-unit-s') || '';
      countEl.textContent = count + (word ? ' ' + word : '');
    }

    if (engine) {
      var totalEl = root.querySelector('[data-bp-total]');
      if (totalEl) totalEl.textContent = engine.formatMoney(total, fmt);
      var oldEl = root.querySelector('[data-bp-old]');
      if (oldEl) {
        if (applies) {
          oldEl.textContent = engine.formatMoney(sum, fmt);
          oldEl.hidden = false;
        } else {
          oldEl.hidden = true;
        }
      }
      var amount = root.querySelector('[data-bp-cta-amount]');
      if (amount) amount.textContent = ' · ' + engine.formatMoney(total, fmt); // §20.6.1: middle-dot separator (was an em-dash)
      // Kit frame badge (§5.2 / amendment 1): the post-discount money total,
      // same value as [data-bp-total]; null-guarded (badge only exists in kit mode).
      var kitTotal = root.querySelector('[data-bp-kit-total]');
      if (kitTotal) kitTotal.textContent = engine.formatMoney(total, fmt);
    }

    // Achieved chip (U6-F1 §3d): the live "−X%" the customer has unlocked, in
    // the total row. Shown only when the discount APPLIES (the header badge is
    // now a static "up to" max, server-rendered). U+2212 matches that server glyph.
    var achieved = root.querySelector('[data-bp-achieved]');
    if (achieved) {
      if (applies) {
        achieved.textContent = '−' + offer.pct + '%';
        achieved.hidden = false;
      } else {
        achieved.hidden = true;
      }
    }

    // Nudge (§20.5): rows to the next better level. Hidden unless a template is
    // set and a better next level exists (resolveOffer.next).
    var nudge = root.querySelector('[data-bp-nudge]');
    if (nudge) {
      var tpl = root.getAttribute('data-bp-nudge-tpl') || '';
      if (tpl && offer.next) {
        nudge.textContent = tpl.replace('{n}', offer.next.needed).replace('{pct}', offer.next.pct);
        nudge.hidden = false;
      } else {
        nudge.hidden = true;
      }
    }

    // Nothing addable -> disabled CTA (plan §12-F).
    var cta = root.querySelector('[data-bp-cta]');
    if (cta) cta.disabled = count === 0;
  }

  function toggleRow(row, root) {
    if (isLocked(row)) return;
    var on = !isOn(row);
    row.classList.toggle('is-on', on);
    row.setAttribute('aria-checked', on ? 'true' : 'false');
    var engine = core();
    if (engine) {
      var blockId = root.getAttribute('data-block-id');
      var st = (engine.state[blockId] = engine.state[blockId] || {});
      st.checked = st.checked || {};
      st.checked[row.getAttribute('data-row-id')] = on;
    }
    recompute(root);
  }

  function handleCta(button, root) {
    var engine = core();
    if (!engine) return;
    var error = root.querySelector('[data-bp-error]');
    if (error) {
      error.hidden = true;
      error.textContent = '';
    }
    var items = [];
    if (root.getAttribute('data-include-main') === 'true' && !root.hasAttribute('data-main-soldout')) {
      var mainId = parseInt(root.getAttribute('data-main-id'), 10);
      if (mainId) items.push({ id: mainId, quantity: 1 });
    }
    checkedCompanions(root).forEach(function (row) {
      var vid = variantIdOf(row);
      if (!vid) return;
      items.push({ id: vid, quantity: parseInt(row.getAttribute('data-qty'), 10) || 1 });
    });
    if (!items.length) return;
    // The applied code IS the resolver's code — display and applied can never
    // diverge (§20.8). Resolved at click against the same displayed count.
    var appliedCode = resolveOffer(root, countOf(root)).code;
    button.disabled = true;
    engine
      .addItems(items)
      .then(function () {
        // U6-readiness: a popup body closes before the after-add matrix so the
        // drawer never stacks under the dialog. Inline roots never match.
        if (root.closest('dialog.overlay')) engine.closeBundleOverlays();
        recompute(root);
        engine.afterAdd(appliedCode, root.getAttribute('data-after-add'), root);
      })
      .catch(function (err) {
        recompute(root);
        if (error) {
          error.textContent = (err && err.message) || 'Error';
          error.hidden = false;
        }
      });
  }

  document.addEventListener('click', function (e) {
    if (!e.target.closest) return;
    var arrow = e.target.closest('.bundle-prods__arrow');
    if (arrow) {
      var aRoot = arrow.closest('[data-bundle-prods]');
      var asc = aRoot && getScroller(aRoot);
      if (asc) {
        // LTR store: scrollLeft semantics (A §21 parity).
        var dir = arrow.classList.contains('bundle-prods__arrow--next') ? 1 : -1;
        asc.scrollBy({ left: dir * 0.8 * asc.clientWidth, behavior: 'smooth' });
      }
      return;
    }
    var cta = e.target.closest('[data-bp-cta]');
    if (cta) {
      var ctaRoot = cta.closest('[data-bundle-prods]');
      if (ctaRoot) handleCta(cta, ctaRoot);
      return;
    }
    var row = e.target.closest('[data-bpp]');
    if (!row) return;
    var root = row.closest('[data-bundle-prods]');
    if (!root) return;
    // Clicks inside the variant select never toggle the row (§5.5).
    if (e.target.closest('[data-bpp-vsel]')) return;
    // Links inside a row (product page) navigate; they never toggle it.
    if (e.target.closest('a')) return;
    toggleRow(row, root);
  });

  document.addEventListener('keydown', function (e) {
    if (e.key !== ' ' && e.key !== 'Enter') return;
    if (!e.target.closest) return;
    if (e.target.closest('[data-bpp-vsel]')) return;
    // Links inside a row (product page) navigate; they never toggle it.
    if (e.target.closest('a')) return;
    var row = e.target.closest('[data-bpp]');
    if (!row) return;
    var root = row.closest('[data-bundle-prods]');
    if (!root) return;
    e.preventDefault();
    toggleRow(row, root);
  });

  /* Variant select: restamp the row's line price from the option's
     data-cents, store the choice (survives SRA swaps), recompute. */
  document.addEventListener('change', function (e) {
    if (!e.target.closest) return;
    var sel = e.target.closest('[data-bpp-vsel]');
    if (!sel) return;
    var row = sel.closest('[data-bpp]');
    var root = sel.closest('[data-bundle-prods]');
    if (!row || !root) return;
    stampRowPrice(row, root);
    var engine = core();
    if (engine) {
      var blockId = root.getAttribute('data-block-id');
      var st = (engine.state[blockId] = engine.state[blockId] || {});
      st.variants = st.variants || {};
      st.variants[row.getAttribute('data-row-id')] = parseInt(sel.value, 10);
    }
    recompute(root);
  });

  /**
   * List vertical cap: with more rows than the visible count, cap the items
   * box at the Nth row's bottom edge (rows[N-1] offsetTop + offsetHeight,
   * relative to row[0] — exactly N rows, no trailing gap; FIX1 F1 math) and
   * scroll the rest (no-JS: uncapped). Rows hidden by the --soldout-hide
   * gate are skipped (offsetParent is null inside display:none).
   */
  function applyVcap(root) {
    if (!root.classList.contains('bundle-prods--list')) return;
    var items = root.querySelector('.bundle-prods__items');
    if (!items) return;
    var rows = rowsOf(root).filter(function (row) {
      return row.offsetParent !== null;
    });
    var styles = getComputedStyle(root);
    var visible = parseInt(styles.getPropertyValue('--bp-visible-d'), 10) || 3;
    if (window.matchMedia('(max-width: 749px)').matches) {
      var mobile = parseInt(styles.getPropertyValue('--bp-visible-m'), 10);
      if (mobile > 0) visible = mobile;
    }
    if (rows.length <= visible) {
      items.style.maxBlockSize = '';
      items.style.overflowY = '';
      return;
    }
    items.style.maxBlockSize = (rows[visible - 1].offsetTop + rows[visible - 1].offsetHeight - rows[0].offsetTop) + 'px';
    items.style.overflowY = 'auto';
  }

  /**
   * Init / SRA restore: re-apply the stored checked set + variant choices to
   * the freshly server-rendered root (locked/sold-out rows keep the server
   * state — required rows stay on, sold-out rows stay off), then recompute
   * and re-cap. Stored variant choices are only applied when the option
   * still exists and is purchasable in the fresh select.
   */
  function initRoot(root) {
    var engine = core();
    var st = engine && engine.state[root.getAttribute('data-block-id')];
    rowsOf(root).forEach(function (row) {
      var rowId = row.getAttribute('data-row-id');
      if (st && st.variants && st.variants[rowId]) {
        var sel = row.querySelector('[data-bpp-vsel]');
        if (sel) {
          var want = String(st.variants[rowId]);
          for (var i = 0; i < sel.options.length; i++) {
            if (sel.options[i].value === want && !sel.options[i].disabled) {
              sel.value = want;
              stampRowPrice(row, root);
              break;
            }
          }
        }
      }
      if (isLocked(row) || isSoldout(row)) return;
      if (st && st.checked && Object.prototype.hasOwnProperty.call(st.checked, rowId)) {
        var on = !!st.checked[rowId];
        row.classList.toggle('is-on', on);
        row.setAttribute('aria-checked', on ? 'true' : 'false');
      }
    });
    recompute(root);
    applyVcap(root);
    syncArrows(root);          // cards-gated (no-op for list)
    applyCardImageSizes(root); // cards-gated sharp-image pass (§12-C)
  }

  function initAll(scope) {
    (scope || document).querySelectorAll('[data-bundle-prods]').forEach(initRoot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
  } else {
    initAll();
  }

  /* SRA restore — B's OWN listener: core's product:variant-changed handler is
     [data-bundle-qty]-scoped (Block A) and stays core-edit-free (§19.4). */
  document.addEventListener('product:variant-changed', function (e) {
    if (e.detail && e.detail.root) initAll(e.detail.root);
  });

  /* Theme-editor re-init (FIX1 F1 lesson, §19.3 day-one): any section/block
     setting change re-renders the section and arrives as shopify:section:load.
     Editor-only event; always registered, zero storefront cost. */
  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
  });

  /* U7 popup refresh (§19.4; A component-bundle-quantity.js:262 mirror): the
     pop-body's layout passes ran at zero while its hidden home was display:none
     — core dispatches bundle:popup-opened right after the synchronous overlay
     open (the overlay emits no event on its moveId path). Re-run the measure +
     state passes on the now-visible B body. The matches() guard ships because
     recompute is stateful and an A-block open on a mixed template fires this
     same document-level event (plan Q3). */
  document.addEventListener('bundle:popup-opened', function (e) {
    var root = e.detail && e.detail.root;
    if (!root || !root.matches || !root.matches('[data-bundle-prods]')) return;
    applyVcap(root);
    syncArrows(root);
    applyCardImageSizes(root);
    recompute(root);
  });

  /* Arrow ends-state on scroll: capture-phase document listener (scroll doesn't
     bubble; capture = delegation, survives SRA swaps), rAF-throttled. A §21 parity. */
  var endsTick = false;
  document.addEventListener('scroll', function (e) {
    var t = e.target;
    if (!t || !t.classList || !t.classList.contains('bundle-prods__scroller')) return;
    if (endsTick) return;
    endsTick = true;
    requestAnimationFrame(function () {
      endsTick = false;
      var root = t.closest('[data-bundle-prods]');
      if (root) syncEnds(root);
    });
  }, true);

  /* Single debounced resize (no ResizeObserver — A policy match): re-sync arrows
     and re-measure card image sizes. Deliberately skips applyVcap (list parity,
     A C9). Both inner calls are cards-gated internally → list roots are no-ops. */
  var resizeTimer;
  window.addEventListener('resize', function () {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function () {
      document.querySelectorAll('[data-bundle-prods]').forEach(function (root) {
        syncArrows(root);
        applyCardImageSizes(root);
      });
    }, 150);
  });
})();
